import { useState } from 'react';
import { Button } from '@/shared/ui/Button';
import type { StrategyStepData } from '../api/onboardingApi';
import styles from './StepForm.module.css';

const TARGET_PATIENT_OPTIONS = [
  {
    value: 'families',
    label: 'Families',
    description: 'Parents with children needing dental care',
  },
  { value: 'seniors', label: 'Seniors', description: 'Adults 65+ looking for dental care' },
  {
    value: 'young_professionals',
    label: 'Young Professionals',
    description: 'Adults 25-40 focused on aesthetics',
  },
  {
    value: 'high_income',
    label: 'High-Income Patients',
    description: 'Premium care and cosmetic procedures',
  },
  {
    value: 'insurance',
    label: 'Insurance Patients',
    description: 'Patients with dental insurance',
  },
  {
    value: 'emergency',
    label: 'Emergency Patients',
    description: 'Walk-ins and urgent dental care',
  },
];

const GOAL_OPTIONS = [
  {
    value: 'new_patients',
    label: 'Attract New Patients',
    description: 'Grow patient base through digital marketing',
  },
  {
    value: 'retention',
    label: 'Improve Retention',
    description: 'Keep existing patients coming back',
  },
  {
    value: 'reviews',
    label: 'Build Online Reputation',
    description: 'Increase positive reviews and ratings',
  },
  {
    value: 'visibility',
    label: 'Improve Search Visibility',
    description: 'Rank higher in local search results',
  },
  {
    value: 'cosmetic',
    label: 'Promote Cosmetic Services',
    description: 'Market high-value cosmetic procedures',
  },
  {
    value: 'brand',
    label: 'Build Brand Awareness',
    description: 'Increase recognition in the community',
  },
];

const BUDGET_OPTIONS = [
  { value: 'under_1k', label: 'Under $1,000/mo' },
  { value: '1k_2.5k', label: '$1,000 - $2,500/mo' },
  { value: '2.5k_5k', label: '$2,500 - $5,000/mo' },
  { value: '5k_10k', label: '$5,000 - $10,000/mo' },
  { value: 'over_10k', label: 'Over $10,000/mo' },
];

interface StrategyStepProps {
  onSubmit: (data: StrategyStepData) => void;
  onBack: () => void;
  isLoading: boolean;
}

export function StrategyStep({ onSubmit, onBack, isLoading }: StrategyStepProps) {
  const [targetPatients, setTargetPatients] = useState<string[]>([]);
  const [mainGoals, setMainGoals] = useState<string[]>([]);
  const [competitors] = useState('');
  const [monthlyBudget, setMonthlyBudget] = useState('');

  const toggleItem = (list: string[], item: string, setter: (v: string[]) => void) => {
    setter(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      targetPatients,
      mainGoals,
      competitors: competitors.split('\n').filter(Boolean),
      monthlyBudget,
    });
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div>
        <p className={styles.sectionTitle}>Who are your target patients?</p>
        <div className={styles.checkboxGroup}>
          {TARGET_PATIENT_OPTIONS.map((opt) => (
            <label key={opt.value} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={targetPatients.includes(opt.value)}
                onChange={() => toggleItem(targetPatients, opt.value, setTargetPatients)}
              />
              <div>
                <div className={styles.checkboxLabel}>{opt.label}</div>
                <div className={styles.checkboxDescription}>{opt.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className={styles.sectionTitle}>What are your main marketing goals?</p>
        <div className={styles.checkboxGroup}>
          {GOAL_OPTIONS.map((opt) => (
            <label key={opt.value} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={mainGoals.includes(opt.value)}
                onChange={() => toggleItem(mainGoals, opt.value, setMainGoals)}
              />
              <div>
                <div className={styles.checkboxLabel}>{opt.label}</div>
                <div className={styles.checkboxDescription}>{opt.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className={styles.sectionTitle}>Monthly Marketing Budget</p>
        <div className={styles.checkboxGroup}>
          {BUDGET_OPTIONS.map((opt) => (
            <label key={opt.value} className={styles.checkboxItem}>
              <input
                type="radio"
                name="budget"
                value={opt.value}
                checked={monthlyBudget === opt.value}
                onChange={() => setMonthlyBudget(opt.value)}
                style={{
                  width: 18,
                  height: 18,
                  accentColor: 'var(--color-primary)',
                  cursor: 'pointer',
                }}
              />
              <div className={styles.checkboxLabel}>{opt.label}</div>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="button" variant="secondary" size="lg" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" isLoading={isLoading} size="lg">
          Continue
        </Button>
      </div>
    </form>
  );
}
